import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    LinearProgress,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Visibility as ViewIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon,
    Schedule as ScheduleIcon
} from '@mui/icons-material';
import Quantino from '../../../components/Quantino/Quantino.jsx';

// History-specific messages
const getHistoryMessages = (attempts, bestScore) => {
    if (attempts === 0) {
        return {
            mood: 'nachdenklich',
            messages: [
                "No attempts yet? Let's get started!",
                "Ready to begin your learning journey?",
                "Time to dive in and learn!"
            ]
        };
    } else if (attempts === 1) {
        return {
            mood: 'motivierend',
            messages: [
                "Your first attempt! Every journey begins with a single step.",
                "Great start! One attempt down, many learnings gained!",
                "Nice! You've taken the first step!",
                "First try completed! You're off to a good start!",
                "One attempt in the books - well done!"
            ]
        };
    } else if (bestScore >= 90) {
        const excellentMoods = ['feiernd', 'stolz_daumen_hoch', 'überrascht'];
        return {
            mood: excellentMoods[Math.floor(Math.random() * excellentMoods.length)],
            messages: [
                `${attempts} attempts and ${bestScore}% best score! You're crushing it!`,
                "Excellence through persistence! Well done!",
                "Your dedication is paying off beautifully!",
                `Amazing! ${bestScore}% - You've truly mastered this!`,
                "Outstanding performance! Your hard work shows!",
                "Incredible achievement! You should be proud!"
            ]
        };
    } else if (bestScore >= 75) {
        const goodMoods = ['freundlich', 'zwinkernd', 'motivierend', 'stolz_daumen_hoch'];
        return {
            mood: goodMoods[Math.floor(Math.random() * goodMoods.length)],
            messages: [
                `${attempts} attempts show your determination!`,
                "Great progress! Your efforts are showing!",
                "Consistent improvement - keep it up!",
                `Solid ${bestScore}%! You're really getting the hang of this!`,
                "Excellent work! Your understanding is growing!",
                "Nice improvement pattern - you're on track!"
            ]
        };
    } else if (bestScore >= 60) {
        const okayMoods = ['motivierend', 'freundlich', 'nachdenklich'];
        return {
            mood: okayMoods[Math.floor(Math.random() * okayMoods.length)],
            messages: [
                `${attempts} attempts show your determination!`,
                "You're making steady progress! Keep it up!",
                "Good effort! Your persistence will pay off!",
                `${bestScore}% is progress! Every step counts!`,
                "You're learning with each attempt!"
            ]
        };
    } else if (attempts >= 5) {
        const strugglingMoods = ['streng', 'motivierend', 'nachdenklich'];
        return {
            mood: strugglingMoods[Math.floor(Math.random() * strugglingMoods.length)],
            messages: [
                "Persistence is the key to mastery!",
                `${attempts} attempts show real dedication!`,
                "Every attempt builds your knowledge!",
                "Your commitment is admirable! Keep going!",
                "True learning spirit! Don't give up!",
                "Each try brings you closer to success!"
            ]
        };
    } else if (attempts >= 3) {
        const challengedMoods = ['traurig', 'nachdenklich', 'motivierend'];
        return {
            mood: challengedMoods[Math.floor(Math.random() * challengedMoods.length)],
            messages: [
                "You're building momentum with each attempt!",
                "Good progress pattern - keep it steady!",
                "Multiple attempts show serious effort!",
                "Learning is a process - you're doing well!",
                "Each try teaches something valuable!",
                "Don't worry - mastery takes time!"
            ]
        };
    } else {
        const beginnerMoods = ['freundlich', 'neutral', 'motivierend'];
        return {
            mood: beginnerMoods[Math.floor(Math.random() * beginnerMoods.length)],
            messages: [
                "Each attempt teaches us something new!",
                "Progress takes time - you're doing great!",
                "Keep exploring - you're on the right path!",
                "Every expert started exactly where you are!",
                "Your learning journey is unique - embrace it!"
            ]
        };
    }
};

// Helper function to format score nicely
const formatScore = (score) => {
    if (score % 1 === 0) {
        // If it's a whole number, return as integer
        return score.toString();
    } else if (score % 0.5 === 0) {
        // If it's a half number (like 1.5), show one decimal
        return score.toFixed(1);
    } else {
        // For other decimals, round to 2 decimal places
        return (Math.round(score * 100) / 100).toString();
    }
};

// Helper function to format percentage nicely
const formatPercentage = (percentage) => {
    return Math.round(percentage * 100) / 100;
};

const SubmissionHistory = ({ 
    history, 
    onBack, 
    onViewSubmission,
    loading 
}) => {
    const [quantino, setQuantino] = useState({
        mood: 'freundlich',
        message: '',
        isVisible: false,
        isBubbleVisible: false,
    });

    useEffect(() => {
        if (history && !loading) {
            const attempts = history.total_attempts || 0;
            const bestScore = history.submissions?.length > 0 ? 
                Math.max(...history.submissions
                    .filter(s => s.percentage !== null)
                    .map(s => s.percentage) || [0]
                ) : 0;
            
            const timer = setTimeout(() => {
                const historyData = getHistoryMessages(attempts, bestScore);
                const randomMessage = historyData.messages[Math.floor(Math.random() * historyData.messages.length)];
                
                setQuantino({
                    mood: historyData.mood,
                    message: randomMessage,
                    isVisible: true,
                    isBubbleVisible: true,
                });

                // Auto-hide bubble after 8 seconds, then keep Quantino visible but smaller
                const hideBubbleTimer = setTimeout(() => {
                    setQuantino(prev => ({...prev, isBubbleVisible: false}));
                    
                    // Keep Quantino visible but smaller after 2 more seconds
                    const minimizeTimer = setTimeout(() => {
                        setQuantino(prev => ({...prev, isVisible: true})); // Keep visible, just smaller
                    }, 2000);

                    return () => clearTimeout(minimizeTimer);
                }, 8000);

                return () => clearTimeout(hideBubbleTimer);
            }, 800);

            return () => clearTimeout(timer);
        }
    }, [history, loading]);
    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <Typography>Loading history...</Typography>
                </Box>
            </Container>
        );
    }

    if (!history || !history.submissions) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Typography>No submission history found.</Typography>
            </Container>
        );
    }

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'in_progress':
                return 'warning';
            case 'abandoned':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return <CheckIcon />;
            case 'in_progress':
                return <ScheduleIcon />;
            case 'abandoned':
                return <CancelIcon />;
            default:
                return null;
        }
    };

    return (
        <Container 
            maxWidth="lg" 
            sx={{ 
                mt: 4, 
                mb: 8,
                transform: 'none', // Prevent any CSS transforms
                zoom: 'normal', // Ensure normal zoom
                overflow: 'visible', // Prevent overflow issues
                position: 'relative' // For absolute positioning of Quantino
            }}
        >
            {/* Quantino positioned absolutely - bottom right, outside flow */}
            <Box sx={{ 
                position: 'fixed', // Fixed positioning to avoid layout issues
                bottom: 20,
                right: 20,
                zIndex: 1000,
                transform: 'scale(0.8)', // Smaller scale
                transformOrigin: 'bottom right',
                pointerEvents: 'none' // Don't interfere with clicks
            }}>
                <Quantino
                    mood={quantino.mood}
                    message={quantino.message}
                    isVisible={true}
                    isBubbleVisible={quantino.isBubbleVisible}
                />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <Button
                    startIcon={<BackIcon />}
                    onClick={onBack}
                    sx={{ 
                        mr: 2,
                        transform: 'none', // Prevent button transforms
                        transition: 'background-color 0.2s ease' // Smooth transition without transforms
                    }}
                >
                    Back
                </Button>
                <Typography variant="h4">
                    Submission History
                </Typography>
            </Box>

            {/* Summary Card - same width as table */}
            <Paper sx={{ p: 3, mb: 4, width: '100%' }}>
                <Typography variant="h6" gutterBottom>
                    Summary
                </Typography>
                <Typography variant="body1">
                    Total attempts: <strong>{history.total_attempts}</strong>
                </Typography>
                {history.submissions.length > 0 && (
                    <Typography variant="body1">
                        Best score: <strong>
                            {formatPercentage(Math.max(...history.submissions
                                .filter(s => s.percentage !== null)
                                .map(s => s.percentage) || [0]
                            ))}%
                        </strong>
                    </Typography>
                )}
            </Paper>

            {/* Table - same width as summary */}
            <Paper sx={{ width: '100%' }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Attempt</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Score</TableCell>
                                <TableCell>Progress</TableCell>
                                <TableCell>Started</TableCell>
                                <TableCell>Completed</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {history.submissions.map((submission, index) => (
                                <TableRow key={submission.submission_id}>
                                    <TableCell>
                                        <Typography variant="subtitle2">
                                            #{history.total_attempts - index}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={getStatusIcon(submission.status)}
                                            label={submission.status.replace('_', ' ').toUpperCase()}
                                            color={getStatusColor(submission.status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {submission.score !== null ? (
                                            <Typography>
                                                {formatScore(submission.score)}/{formatScore(submission.max_score)}
                                            </Typography>
                                        ) : (
                                            <Typography color="text.secondary">-</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {submission.percentage !== null ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 120 }}>
                                                <Box sx={{ width: '100%', mr: 1 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={submission.percentage}
                                                        color={submission.percentage >= 60 ? 'success' : 'error'}
                                                        sx={{ height: 8, borderRadius: 4 }}
                                                    />
                                                </Box>
                                                <Box sx={{ minWidth: 35 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatPercentage(submission.percentage)}%
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Typography color="text.secondary">-</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatDate(submission.started_at)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {submission.completed_at ? (
                                            <Typography variant="body2">
                                                {formatDate(submission.completed_at)}
                                            </Typography>
                                        ) : (
                                            <Typography color="text.secondary">-</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {submission.status === 'completed' && (
                                            <Tooltip title="View detailed results">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onViewSubmission(submission.submission_id)}
                                                >
                                                    <ViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Container>
    );
};

export default SubmissionHistory;
